// ── User edit ("/users/:id/edit") — the focused edit screen (Wave 1) ───────
// The user is read from the dedicated users.detail endpoint through
// useHonestQuery -> <HonestState>: a gate / permission-denied / unavailable /
// error NEVER reads as an empty edit shell, and a not-found id renders an
// honest "User not found", NEVER a leak.
//
// THE ID IS A STRING — passed VERBATIM (never Number()'d). STAFF-ONLY (the route
// manifest denies non-staff). Granting the `admin` role requires super_admin
// (enforced by the form's role picker + server re-check). The edit form opens
// inline on this focused screen; Save toasts ONLY after the backend confirms,
// then navigates back to the user list.

import { Button, FormSurface, useToast } from "@vjal/ui";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useUserLifecycleMutations } from "../../hooks/useCoachingQueries";
import { evaluateActionCapability } from "../../domain/capabilities";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingUserDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { UserFormModal, type UserFormValues } from "./user-form-modal";
import { userWriteBody } from "./users-write";
import { UserDeactivateModal } from "./user-deactivate-modal";
import { isValidUserId, userDisplayName } from "./user-detail-helpers";
const services = getCoachingServices();

export function UserEditPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const navigate = useNavigate();
  const mutations = useUserLifecycleMutations();
  const allowUpdate = evaluateActionCapability(role, "users:update").allowed;

  // The path is /users/:id/edit — the id sits after the "users" segment.
  const userId = liveRecordIdFromPath(routePath, "users");
  const validId = isValidUserId(userId);

  const [formOpen, setFormOpen] = useState(false);
  const [formAutoOpened, setFormAutoOpened] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const query = useHonestQuery<CoachingUserDto>(
    coachingQueryKeys.userDetail(userId ?? ""),
    () => services.users.detail(userId as string),
    { enabled: validId }
  );
  // Keep the resolved record reference stable while the detail query is cached.
  const user = query.data ?? null;

  // Open the edit form once, when the user first resolves on a write-allowed
  // screen (render adjustment). The separate flag keeps it closed after Cancel;
  // guarding on !formOpen re-opened it on the next render.
  if (allowUpdate && user && !formAutoOpened) {
    setFormAutoOpened(true);
    setFormOpen(true);
  }

  function submitEdit(values: UserFormValues) {
    if (!user) return Promise.reject(new Error("No user loaded"));
    return mutations.update
      .mutateAsync({
        userId: user.id,
        body: userWriteBody(values)
      })
      .then((updated) => {
        mutations.update.reset();
        toast.success(`User ${updated.name ?? values.name} updated`);
        navigate("/users");
        return updated;
      });
  }

  function submitDeactivate() {
    if (!user) return Promise.reject(new Error("No user loaded"));
    return mutations.deactivate.mutateAsync(user.id).then((result) => {
      setDeactivateOpen(false);
      mutations.deactivate.reset();
      toast.success(`User ${userDisplayName(user)} deactivated`);
      navigate("/users");
      return result;
    });
  }

  // ── Bad/absent id, not-found, or non-ready posture → one honest body. ───────
  if (!validId || query.posture !== null || !user) {
    const posture = !validId ? "empty" : (query.posture ?? "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="Edit user" className="col-span-12" data-testid="user-edit-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "User not found" : undefined}
            message={notFound ? "This user doesn't exist, or isn't in your workspace." : undefined}
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/users" className="text-link no-underline hover:underline">
                  Back to users
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }
  const lifecycleStatus = user.membershipStatus ?? user.status;

  return (
    <section aria-label="Edit user" className="col-span-12 flex flex-col gap-4" data-testid="user-edit-page">
      {/* F3: real @vjal/ui FormSurface for Form-1 user-edit route. */}
      <FormSurface
        title={`Edit ${userDisplayName(user)}`}
        titleExactName="Edit user title"
        hideSubmit
        className="col-span-12"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/users" className="text-link no-underline hover:underline">
            ← All users
          </Link>
          <span className="text-sm text-ink-muted">Reference {user.id}</span>
          {allowUpdate && (
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" onClick={() => setFormOpen(true)} aria-label="Edit user fields">
                Edit fields
              </Button>
              <Button
                variant="danger"
                className={destructiveButtonClass}
                onClick={() => setDeactivateOpen(true)}
                disabled={lifecycleStatus === "inactive" || lifecycleStatus === "removed"}
                aria-label="Deactivate user"
              >
                Deactivate
              </Button>
            </div>
          )}
        </div>
      </FormSurface>

      {allowUpdate && (
        <>
          <UserFormModal
            open={formOpen}
            mode="edit"
            actorRole={role}
            user={user}
            onClose={() => {
              setFormOpen(false);
              mutations.update.reset();
            }}
            onSubmit={submitEdit}
            submitting={mutations.update.isPending}
            submitError={mutations.update.error}
          />
          <UserDeactivateModal
            open={deactivateOpen}
            user={user}
            onClose={() => {
              setDeactivateOpen(false);
              mutations.deactivate.reset();
            }}
            onConfirm={submitDeactivate}
            submitting={mutations.deactivate.isPending}
            submitError={mutations.deactivate.error}
          />
        </>
      )}
    </section>
  );
}
